import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import './CheckoutSuccessPage.css';

const CheckoutSuccessPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const order = location.state?.order;

  // Formatar moeda
  const formatCurrency = (value) => {
    const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numValue / 100);
  };

  // Formatar data
  const formatDate = (dateString) => {
    if (!dateString) return 'Data não disponível';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Data inválida';
    }
  };

  // Obter nome do método de pagamento
  const getPaymentMethodName = (method) => {
    const methods = {
      'credit_card': 'Cartão de Crédito',
      'debit_card': 'Cartão de Débito',
      'boleto': 'Boleto Bancário',
      'pix': 'PIX',
      'afterpay': 'AfterPay'
    };
    return methods[method] || method || 'Não informado';
  };

  // Obter ícone do método de pagamento
  const getPaymentMethodIcon = (method) => {
    const icons = {
      'credit_card': 'mdi:credit-card',
      'debit_card': 'mdi:credit-card-outline',
      'boleto': 'mdi:barcode',
      'pix': 'mdi:qrcode',
      'afterpay': 'mdi:clock-outline'
    };
    return icons[method] || 'mdi:cash';
  };

  // Estado de pagamento
  const getPaymentStatus = () => {
    const status = order?.sale_status || order?.payment_status;
    if (status === 'paid' || status === 'processing') {
      return { text: 'Pago', color: 'success', icon: 'mdi:check-circle' };
    }
    if (status === 'pending' || status === 'pending_payment') {
      return { text: 'Aguardando Pagamento', color: 'warning', icon: 'mdi:clock-outline' };
    }
    return { text: 'Confirmado', color: 'success', icon: 'mdi:check-circle' };
  };

  // Ir para acompanhar pedido
  const handleTrackOrder = () => {
    // Verificar se usuário está logado
    const token = localStorage.getItem('auth_token');
    if (token) {
      navigate('/my-purchases');
    } else {
      // Se não está logado, redirecionar para login com retorno para my-purchases
      navigate('/login', { state: { returnTo: '/my-purchases' } });
    }
  };

  // Tela de erro quando não tem dados do pedido
  if (!order) {
    return (
      <div className="checkout-success-page">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6">
              <div className="success-card text-center">
                <div className="error-icon-wrapper">
                  <Icon icon="mdi:alert-circle-outline" />
                </div>
                <span className="success-title" style={{ color: '#f59e0b' }}>Pedido não encontrado</span>
                <p className="text-muted mb-4">
                  Não foi possível carregar os detalhes do pedido.
                  Se você acabou de fazer uma compra, verifique seu email.
                </p>
                <button className="btn btn-primary btn-lg" onClick={() => navigate('/')}>
                  <Icon icon="mdi:home" className="me-2" />
                  Voltar para Home
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const paymentStatus = getPaymentStatus();
  const isAfterPay = order.payment_method === 'afterpay' || order.payment_method === 'after_pay';

  return (
    <div className="checkout-success-page">
      <div className="container py-4">
        <div className="row justify-content-center">
          <div className="col-lg-6 col-md-8">

            {/* Card Principal de Sucesso */}
            <div className="success-card">
              {/* Header com animação */}
              <div className="success-header">
                <div className="success-icon-wrapper">
                  <Icon icon={isAfterPay ? "mdi:calendar-check" : "mdi:check"} className="success-icon" />
                </div>
                <span className="success-title">
                  {isAfterPay ? 'Pedido Confirmado!' : 'Pagamento Confirmado!'}
                </span>
                <p className="success-subtitle">
                  {isAfterPay
                    ? `Pedido #${order.id} agendado com sucesso`
                    : `Pedido #${order.id} realizado com sucesso`
                  }
                </p>
                {isAfterPay && (
                  <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                    O pagamento será realizado após a entrega do produto
                  </p>
                )}
              </div>

              {/* Comprovante */}
              <div className="receipt-container">
                <div className="receipt-header">
                  <Icon icon="mdi:receipt-text" className="me-2" />
                  COMPROVANTE DE COMPRA
                </div>

                {/* Produto */}
                <div className="receipt-section">
                  <div className="receipt-product">
                    <img
                      src={order.product?.image || order.product?.image_url || "https://via.placeholder.com/60x60?text=Produto"}
                      alt={order.product?.name}
                      className="receipt-product-image"
                      onError={(e) => {
                        e.target.src = "https://via.placeholder.com/60x60?text=Produto";
                      }}
                    />
                    <div className="receipt-product-info">
                      <span className="receipt-product-name">{order.product?.name || 'Produto'}</span>
                      <span className="receipt-product-qty">Quantidade: 1</span>
                    </div>
                    <div className="receipt-product-price">
                      {formatCurrency(order.total_amount)}
                    </div>
                  </div>
                </div>

                <div className="receipt-divider"></div>

                {/* Detalhes do Pagamento */}
                <div className="receipt-section">
                  <div className="receipt-row">
                    <span className="receipt-label">Forma de Pagamento</span>
                    <span className="receipt-value">
                      <Icon icon={getPaymentMethodIcon(order.payment_method)} className="me-1" />
                      {getPaymentMethodName(order.payment_method)}
                    </span>
                  </div>
                  {order.payment_method === 'credit_card' && order.payment_info?.installments && (
                    <div className="receipt-row">
                      <span className="receipt-label">Parcelas</span>
                      <span className="receipt-value">{order.payment_info.installments}x</span>
                    </div>
                  )}
                  <div className="receipt-row">
                    <span className="receipt-label">Status</span>
                    <span className={`receipt-status receipt-status-${paymentStatus.color}`}>
                      <Icon icon={paymentStatus.icon} className="me-1" />
                      {paymentStatus.text}
                    </span>
                  </div>
                  <div className="receipt-row">
                    <span className="receipt-label">Data</span>
                    <span className="receipt-value">{formatDate(order.created_at)}</span>
                  </div>
                </div>

                <div className="receipt-divider"></div>

                {/* Comprador */}
                <div className="receipt-section">
                  <div className="receipt-section-title">
                    <Icon icon="mdi:account" className="me-1" />
                    Comprador
                  </div>
                  <div className="receipt-row">
                    <span className="receipt-label">Nome</span>
                    <span className="receipt-value">{order.customer?.name || 'Não informado'}</span>
                  </div>
                  <div className="receipt-row">
                    <span className="receipt-label">Email</span>
                    <span className="receipt-value receipt-email">{order.customer?.email || 'Não informado'}</span>
                  </div>
                </div>

                <div className="receipt-divider-bold"></div>

                {/* Total */}
                <div className="receipt-total">
                  <span>TOTAL</span>
                  <span className="receipt-total-value">{formatCurrency(order.total_amount)}</span>
                </div>
              </div>

              {/* Email Notification */}
              <div className="email-notification">
                <Icon icon="mdi:email-check" className="email-notification-icon" />
                <span>Enviamos um comprovante para <strong>{order.customer?.email}</strong></span>
              </div>
            </div>

            {/* Próximos Passos */}
            <div className="next-steps-card">
              <div className="next-steps-header">
                <Icon icon="mdi:clipboard-check-outline" className="me-2" />
                Próximos Passos
              </div>
              <div className="next-steps-list">
                <div className="next-step-item">
                  <div className="next-step-number">1</div>
                  <div className="next-step-content">
                    <span className="next-step-title">Verifique seu email</span>
                    <span className="next-step-desc">
                      {isAfterPay
                        ? 'Enviamos a confirmação do pedido e os detalhes da entrega'
                        : 'Enviamos os detalhes da compra e instruções de acesso'
                      }
                    </span>
                  </div>
                </div>
                <div className="next-step-item">
                  <div className="next-step-number">2</div>
                  <div className="next-step-content">
                    <span className="next-step-title">
                      {isAfterPay ? 'Aguarde a entrega' : 'Acesse seu produto'}
                    </span>
                    <span className="next-step-desc">
                      {isAfterPay
                        ? 'Seu pedido será preparado e enviado. Você receberá atualizações por email'
                        : 'Siga as instruções do email para acessar seu produto digital'
                      }
                    </span>
                  </div>
                </div>
                <div className="next-step-item">
                  <div className="next-step-number">3</div>
                  <div className="next-step-content">
                    <span className="next-step-title">
                      {isAfterPay ? 'Pague na entrega' : 'Acompanhe seu pedido'}
                    </span>
                    <span className="next-step-desc">
                      {isAfterPay
                        ? 'O pagamento será feito no momento da entrega do produto'
                        : 'Acesse "Minhas Compras" para ver o status e histórico'
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Botão de Ação */}
            <div className="action-buttons">
              <button className="btn btn-primary btn-lg btn-action" onClick={handleTrackOrder}>
                <Icon icon="mdi:package-variant" className="me-2" />
                Acompanhar Pedido
              </button>
            </div>

            {/* Footer de Suporte */}
            <div className="support-footer">
              <Icon icon="mdi:headset" className="me-2" />
              Dúvidas? Entre em contato com o suporte do produtor
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
